import {announce} from '@github-ui/aria-live'
import {useDebounce} from '@github-ui/use-debounce'
import {PageLayout} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react'

import type {RemoveMemexItemRequest, UnarchiveMemexItemRequest} from '../../../api/memex-items/contracts'
import {ItemType} from '../../../api/memex-items/item-type'
import {getInitialState} from '../../../helpers/initial-state'
import {useConfirmRemoveItems} from '../../../hooks/use-remove-memex-items-with-id'
import {useShowToastOnError} from '../../../hooks/use-show-toast-on-error'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {useMemexItems} from '../../../state-providers/memex-items/use-memex-items'
import {type TransformedPaginatedData, usePaginatedArchivedItemsQuery} from '../hooks/use-paginated-archive-items-query'
import {useRemoveArchivedItemsMutation} from '../hooks/use-remove-archived-item-mutation'
import {useRestoreItemsMutation} from '../hooks/use-restore-archived-item-mutation'
import {
  initialPaginatedArchiveState,
  paginatedArchiveReducer,
  setItemSelection,
  setNextCursor,
  setPreviousCursor,
} from '../reducers/paginated-archive-reducer'
import {ArchiveResources} from '../strings'
import {ArchiveList} from './archive-list'
import {ArchiveQueryBuilder} from './archive-query-builder'
import {PaginatedArchiveFooter} from './paginated-archive-footer'
import {PaginatedArchiveHeader} from './paginated-archive-header'
import {PaginatedArchiveItem} from './paginated-archive-item'
import {PaginatedArchiveMutationInProgressDialog} from './paginated-archive-mutation-in-progress-dialog'
import styles from './paginated-archive-view.module.css'

const getDataOrDefaults = (data?: TransformedPaginatedData) => ({
  filteredItems: data?.itemModels ?? [],
  hasNextPage: data?.pageInfo.hasNextPage ?? false,
  endCursor: data?.pageInfo.endCursor,
  totalCount: data?.totalCount ?? {value: 0, isApproximate: false},
})

export function PaginatedArchiveView() {
  const [state, dispatch] = useReducer(paginatedArchiveReducer, initialPaginatedArchiveState)
  const {data, isInitialLoading, isFetching, isSuccess} = usePaginatedArchivedItemsQuery({
    after: state.currentCursor,
    q: state.debouncedFilterValue,
  })

  const {filteredItems, hasNextPage, endCursor, totalCount} = useMemo(() => getDataOrDefaults(data), [data])
  const hasPreviousPage = state.previousCursors.length > 0
  const onFetchNextPage =
    hasNextPage && endCursor ? () => dispatch(setNextCursor(endCursor, filteredItems.length)) : undefined
  const onFetchPreviousPage = hasPreviousPage ? () => dispatch(setPreviousCursor()) : undefined

  const selectedItemsState = useMemo(
    () => (state.itemSelection === 'all_on_server' ? 'all_on_server' : new Set(state.itemSelection)),
    [state.itemSelection],
  )

  const selectableItemIds = useMemo(
    () =>
      filteredItems
        .filter(item => {
          const isRedacted = item.contentType === ItemType.RedactedItem
          const wasRecentlyRestored = !item.archived?.archivedAt
          return !(isRedacted || wasRecentlyRestored)
        })
        .map(item => item.id),
    [filteredItems],
  )

  const toggleSelectAllItemsInPage = useCallback(() => {
    if (selectedItemsState === 'all_on_server' || selectableItemIds.length === selectedItemsState.size) {
      dispatch(setItemSelection([]))
    } else {
      dispatch(setItemSelection(selectableItemIds))
    }
  }, [selectableItemIds, selectedItemsState])

  const selectMatchingItemsOnServer = useCallback(() => {
    dispatch(setItemSelection('all_on_server'))
  }, [])

  const {items} = useMemexItems()
  const projectItemLimit = getInitialState().projectLimits.projectItemLimit
  const canRestoreSingleItem = items.length + 1 <= projectItemLimit
  const canBulkRestore = getCanBulkRestore(projectItemLimit, selectedItemsState, items, totalCount.value)

  const onToggleSelection = useCallback(
    (archivedItem: MemexItemModel) => {
      if (selectedItemsState === 'all_on_server') {
        dispatch(setItemSelection(selectableItemIds.filter(id => id !== archivedItem.id)))
      } else {
        const selectedIdsSet = new Set(selectedItemsState)
        if (selectedIdsSet.has(archivedItem.id)) {
          selectedIdsSet.delete(archivedItem.id)
        } else {
          selectedIdsSet.add(archivedItem.id)
        }
        dispatch(setItemSelection(Array.from(selectedIdsSet)))
      }
    },
    [selectableItemIds, selectedItemsState],
  )

  const onError = useShowToastOnError()
  const {mutate: restoreItems, isPending: restoreIsMutating} = useRestoreItemsMutation()
  const onRestoreItems = useCallback(
    async (request: UnarchiveMemexItemRequest) => {
      restoreItems(request, {
        onSuccess: () => {
          if (state.itemSelection === 'all_on_server' || state.itemSelection.length === selectableItemIds.length) {
            dispatch(setItemSelection([]))
          } else {
            const ids = 'memexProjectItemIds' in request ? request.memexProjectItemIds : []
            dispatch(setItemSelection(state.itemSelection.filter(id => !ids.includes(id))))
          }
        },
        onError,
      })
    },
    [onError, restoreItems, selectableItemIds.length, state.itemSelection],
  )
  const onRestoreSelectedItems = useCallback(() => {
    if (selectedItemsState === 'all_on_server') {
      onRestoreItems({q: state.debouncedFilterValue})
    } else {
      onRestoreItems({memexProjectItemIds: Array.from(selectedItemsState)})
    }
  }, [onRestoreItems, selectedItemsState, state.debouncedFilterValue])

  const {mutate: removeItems, isPending: removeIsMutating} = useRemoveArchivedItemsMutation()
  const {confirmRemoveItems} = useConfirmRemoveItems()
  const onRemoveItems = useCallback(
    async (request: RemoveMemexItemRequest) => {
      let ids: Array<number> = []
      let content: string | undefined = undefined
      if ('memexProjectItemIds' in request) {
        ids = request.memexProjectItemIds
      } else {
        content = ArchiveResources.deleteAllMatchingItemsConfirmation
      }
      if (await confirmRemoveItems(ids, content ? {content} : undefined)) {
        removeItems(request, {
          onSuccess: () => {
            if (state.itemSelection === 'all_on_server' || state.itemSelection.length === selectableItemIds.length) {
              dispatch(setItemSelection([]))
            } else {
              dispatch(setItemSelection(state.itemSelection.filter(id => !ids.includes(id))))
            }
          },
          onError,
        })
      }
    },
    [confirmRemoveItems, onError, removeItems, selectableItemIds.length, state.itemSelection],
  )

  const onRemoveSelectedItems = useCallback(() => {
    if (selectedItemsState === 'all_on_server') {
      onRemoveItems({q: state.debouncedFilterValue, scope: 'archived'})
    } else {
      onRemoveItems({memexProjectItemIds: Array.from(selectedItemsState)})
    }
  }, [onRemoveItems, selectedItemsState, state.debouncedFilterValue])

  const renderItem = useCallback(
    (archivedItem: MemexItemModel) => (
      <PaginatedArchiveItem
        archivedItem={archivedItem}
        columnData={archivedItem.columns}
        canRestore={canRestoreSingleItem}
        projectItemLimit={projectItemLimit}
        isSelected={selectedItemsState === 'all_on_server' || selectedItemsState.has(archivedItem.id)}
        onToggleSelection={() => onToggleSelection(archivedItem)}
        onRestoreItems={onRestoreItems}
        onRemoveItems={onRemoveItems}
        mutationLoading={restoreIsMutating || removeIsMutating}
      />
    ),
    [
      canRestoreSingleItem,
      onRemoveItems,
      onRestoreItems,
      onToggleSelection,
      projectItemLimit,
      removeIsMutating,
      restoreIsMutating,
      selectedItemsState,
    ],
  )

  const [filterInputValue, setFilterInputValue] = useState('')

  const debouncedSetFilterAction = useDebounce(
    (newFilterValue: string) => dispatch({type: 'SET_DEBOUNCED_FILTER_VALUE', debouncedFilterValue: newFilterValue}),
    225,
  )

  const setFilterValue = useCallback(
    (newFilterValue: string) => {
      setFilterInputValue(newFilterValue)
      debouncedSetFilterAction(newFilterValue)
    },
    [debouncedSetFilterAction],
  )

  const searchInputRef = useRef<HTMLInputElement>(null)

  // If there is a filter, assume items exist in the archive.
  // If there's no filter, rely on the actual item count.
  // This allows ArchiveList to show the correct empty state.
  const hasArchive = filteredItems.length > 0 || !!state.debouncedFilterValue

  useFilterAnnouncement(
    {
      isSuccess,
      isFetching,
      debouncedFilterValue: state.debouncedFilterValue,
      filterInputValue,
      itemCount: filteredItems.length,
    },
    state.debouncedFilterValue,
  )

  const lastCursor = state.previousCursors[state.previousCursors.length - 1]
  const previousPagesItemCount = lastCursor ? lastCursor.previousPagesItemCount : 0

  const showInProgressDialog =
    (restoreIsMutating || removeIsMutating) && (selectedItemsState === 'all_on_server' || selectedItemsState.size > 10)

  return (
    <PageLayout containerWidth="large" rowGap="condensed" className={styles.PageLayout}>
      <PageLayout.Content>
        <div className={styles.Box}>
          <ArchiveQueryBuilder
            ref={searchInputRef}
            onChange={value => setFilterValue(value)}
            value={filterInputValue}
          />
          <PaginatedArchiveHeader
            canBulkRestore={canBulkRestore}
            projectItemLimit={projectItemLimit}
            loading={isFetching}
            restoreIsMutating={restoreIsMutating}
            removeIsMutating={removeIsMutating}
            filteredItemCount={filteredItems.length}
            itemSelection={selectedItemsState === 'all_on_server' ? 'all_on_server' : selectedItemsState.size}
            hasSelectedAllItemsInPage={
              selectedItemsState === 'all_on_server' || selectedItemsState.size === selectableItemIds.length
            }
            hasMoreItemsOnServer={hasNextPage || hasPreviousPage}
            onToggleSelectAll={toggleSelectAllItemsInPage}
            onRestoreSelectedItems={onRestoreSelectedItems}
            onRemoveSelectedItems={onRemoveSelectedItems}
            selectMatchingItemsOnServer={selectMatchingItemsOnServer}
            totalCount={totalCount}
            previousPagesItemCount={previousPagesItemCount}
          />
        </div>
        <ArchiveList
          filteredItems={filteredItems}
          loaded={!isInitialLoading}
          hasArchive={hasArchive}
          renderItem={renderItem}
          className={clsx(filteredItems.length > 0 && styles.ArchiveList)}
        />
        <PaginatedArchiveFooter
          hasResults={filteredItems.length > 0}
          onFetchNextPage={onFetchNextPage}
          onFetchPreviousPage={onFetchPreviousPage}
          mutationIsLoading={removeIsMutating || restoreIsMutating}
        />
        {showInProgressDialog && (
          <PaginatedArchiveMutationInProgressDialog
            headerText={restoreIsMutating ? 'Restoring items...' : 'Deleting items...'}
          />
        )}
      </PageLayout.Content>
    </PageLayout>
  )
}

function getCanBulkRestore(
  projectItemLimit: number,
  selectedItemsState: Set<number> | 'all_on_server',
  items: Readonly<Array<MemexItemModel>>,
  minimumTotalCount: number,
) {
  if (selectedItemsState === 'all_on_server') {
    return items.length + minimumTotalCount < projectItemLimit
  } else {
    return items.length + selectedItemsState.size <= projectItemLimit
  }
}

type FilterAnnouncementParams = {
  isSuccess: boolean
  isFetching: boolean
  debouncedFilterValue: string
  filterInputValue: string
  itemCount: number
}

// Returns the message to announce once a filter request has genuinely settled, or null if the
// guards are not met. Guards:
//   isSuccess     — the request settled successfully; a failed request also clears the list but
//                   does not mean "no matches", and keepPreviousData shows stale rows while fetching.
//   !isFetching   — no request in flight (avoids announcing stale data).
//   debouncedFilterValue non-empty — suppress announcements when no filter is active (e.g. initial
//                   page load with items, or after the user clears the filter).
//   debounced === input — a keystroke late in the delay window cancels the pending timer instead of
//                   announcing a result the user has already moved on from.
export function getFilterAnnouncementMessage({
  isSuccess,
  isFetching,
  debouncedFilterValue,
  filterInputValue,
  itemCount,
}: FilterAnnouncementParams): string | null {
  if (!isSuccess || isFetching || !debouncedFilterValue || filterInputValue !== debouncedFilterValue) return null
  return itemCount === 0 ? ArchiveResources.noFilteredItems : ArchiveResources.filteredItemCount(itemCount)
}

// query-builder-element emits its own screen-reader feedback ~150ms after input. We delay the
// filter announcement past that so the two don't collide; the buffer is empirical.
export const FILTER_ANNOUNCEMENT_DELAY_MS = 1200

function useFilterAnnouncement(
  {isSuccess, isFetching, debouncedFilterValue, filterInputValue, itemCount}: FilterAnnouncementParams,
  filterValue: string,
) {
  // `filterValue` is a dependency so a new filter re-triggers the announcement even when the
  // derived message stays the same (e.g. two consecutive empty-result queries).
  useEffect(() => {
    const msg = getFilterAnnouncementMessage({isSuccess, isFetching, debouncedFilterValue, filterInputValue, itemCount})
    if (!msg) return
    const timeoutId = setTimeout(() => announce(msg), FILTER_ANNOUNCEMENT_DELAY_MS)
    return () => clearTimeout(timeoutId)
  }, [isSuccess, isFetching, debouncedFilterValue, filterInputValue, itemCount, filterValue])
}
